import React from 'react';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import {
  BaseSingleInputFieldProps,
  DateValidationError,
  FieldSection,
  LocalizationProvider,
  DatePicker as MUIDatePicker,
  DatePickerProps,
  UseDateFieldProps,
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { format } from 'date-fns';

interface TWDatePickerProps {
  value: Date | undefined | null;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  onChange?: (date: Date | null) => void;
  error?: string;
  testId?: string;
}

interface ButtonFieldProps extends UseDateFieldProps<Date, false>, BaseSingleInputFieldProps<Date | null, Date, FieldSection, false, DateValidationError> {
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
}

function ButtonField(props: ButtonFieldProps) {
  const { setOpen, value, id, disabled, InputProps: { ref } = {}, inputProps: { 'aria-label': ariaLabel } = {} } = props;

  return (
    <button
      data-cy="date-picker"
      type="button"
      className={`rounded-lg border border-gray-300 bg-white shadow-xs flex place-items-center px-3.5 py-2.5 w-full ${
        disabled ? 'text-gray-300' : 'text-gray-700 font-medium'
      }`}
      onClick={() => setOpen?.((prev) => !prev)}
      id={id}
      disabled={disabled}
      ref={ref}
      aria-label={ariaLabel}
    >
      <CalendarDaysIcon className="w-5 h-5 mr-2" /> {value ? format(value ?? 0, 'EEEE dd MMM yyyy') : 'Pick a date'}
    </button>
  );
}

function ButtonDatePicker(props: Omit<DatePickerProps<Date>, 'open' | 'onOpen' | 'onClose'>) {
  const [open, setOpen] = React.useState(false);

  return (
    <MUIDatePicker
      slots={{ field: ButtonField, ...props.slots }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      slotProps={{ field: { setOpen } as any }}
      {...props}
      value={props.value ? props.value : new Date()}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
    />
  );
}

const DatePicker = ({ testId, value, minDate, maxDate, disabled = false, onChange, error }: TWDatePickerProps) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ButtonDatePicker data-cy={testId} value={value} onChange={onChange} minDate={minDate} maxDate={maxDate} disabled={disabled} />
      {error && (
        <p className="mt-2 text-sm text-error-600 dark:text-green-500">
          <span className="font-medium">{error}</span>
        </p>
      )}
    </LocalizationProvider>
  );
};

export default DatePicker;
